import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'

import styled from '@emotion/styled'

const COLOR1 = '#f2f2f2'
const COLOR2 = '#CFEAF1'
const COLOR_INDICATOR = '#16A7CD'

const background = (pos, count, stepWidth) => {
  return pos === 0
    ? `background-image: linear-gradient(to right, ${COLOR1} 0px, ${COLOR1});`
    : pos === count - 1
    ? `background-image: linear-gradient(to right, ${COLOR2} 0px, ${COLOR2});`
    : `background-image: linear-gradient(to right, ${COLOR2} 0px, ${COLOR2} ${pos *
        stepWidth}px, ${COLOR1} ${pos * stepWidth}px );`
}

const DiscreteSliderContainer = styled.span`
  display: inline-block;
  height: 6px;
  margin-left: 8px;
  margin-right: 8px;
  margin-bottom: 2px;

  transition: background 0.15s;
  position: relative;

  ${({ pos, count, stepWidth }) => `width: ${stepWidth * (count - 1)}px;
  
    ${background(pos, count, stepWidth)}
  `}

  &:before {
    content: '';
    display: inline-block;
    position: absolute;
    left: -8px;
    width: 8px;
    height: 6px;
    transition: background 0.15s;
    background-color: ${({ pos }) => (pos === 0 ? COLOR1 : COLOR2)};
    border-bottom-left-radius: 3px;
    border-top-left-radius: 3px;
  }

  &:after {
    content: '';
    display: inline-block;
    position: absolute;
    right: -8px;
    width: 8px;
    height: 6px;
    transition: background 0.15s;
    background-color: ${({ pos, count }) =>
      pos === count - 1 ? COLOR2 : COLOR1};
    border-bottom-right-radius: 3px;
    border-top-right-radius: 3px;
  }
`

const DiscreteSliderIndicator = styled.span`
  position: absolute;
  z-index: 1;
  display: inline-block;
  width: 16px;
  height: 16px;

  bottom: -4px;
  left: 0;
  margin-left: -8px;
  border-radius: 50%;
  background-color: ${COLOR_INDICATOR};

  transition: left 0.15s ease-in-out;
  ${({ pos, stepWidth }) => `left: ${pos * stepWidth}px;`}
`

const StyledDiscreteSliderSelector = styled.button`
  position: absolute;
  z-index: 2;
  display: inline-block;

  width: 16px;
  height: 16px;
  margin-left: -8px;
  bottom: -4px;
  background: transparent;
  border: none;
  cursor: pointer;
  ${({ pos, stepWidth }) => `left: ${pos * stepWidth}px;`}
`

const DiscreteSliderSelector = ({ pos, value, onChange, stepWidth }) => {
  const handleClick = () => {
    typeof onChange === 'function' && onChange(value)
  }

  return (
    <StyledDiscreteSliderSelector
      pos={pos}
      onClick={handleClick}
      stepWidth={stepWidth}
    />
  )
}

DiscreteSliderSelector.propTypes = {
  pos: PropTypes.number.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  stepWidth: PropTypes.number.isRequired
}

export const DiscreteSlider = ({
  value,
  options,
  onChange,
  stepWidth,
  className
}) => {
  const pos = options.findIndex(o => o === value)

  return (
    <DiscreteSliderContainer
      pos={pos}
      count={options.length}
      stepWidth={stepWidth}
      className={className}
      data-mu-test={classnames(
        'e-discrete-slider',
        `e-discrete-slider-value--${value}`
      )}
    >
      <DiscreteSliderIndicator pos={pos} stepWidth={stepWidth} />
      {options.map((v, i) => (
        <DiscreteSliderSelector
          key={v}
          pos={i}
          value={v}
          onChange={onChange}
          stepWidth={stepWidth}
          data-mu-test={`e-discrete-slider__selector-value--${value}`}
        />
      ))}
    </DiscreteSliderContainer>
  )
}

DiscreteSlider.defaultProps = {
  stepWidth: 52
}

DiscreteSlider.propTypes = {
  className: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  onChange: PropTypes.func,
  stepWidth: PropTypes.number
}
